import React, { useState } from 'react'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { STEP_BTN } from 'common/tw/ui'
import DiscountPill from './DiscountPill'

interface PurchaseDetailsProps {
  planName: string
  priceDisplay: string
  originalPriceDisplay?: string
  discountLabel?: string
  includedLabel: string
  includedValue?: number
  extraLabel: string
  extraUnit: string
  seatPrice?: string
  originalSeatPrice?: string
  count: number
  minCount: number
  onCountChange: (next: number) => void
  billingLabel: string
  expiresAt?: number
  total: number
  originalTotal?: number
  perSeat: boolean
}

function Row({
  label,
  hint,
  hintNode,
  children,
}: {
  label: string
  hint?: string
  hintNode?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className='flex items-center justify-between border-b border-line-200 py-3 last:border-b-0'>
      <div>
        <p className='text-13 text-ink-500'>{label}</p>
        {hint && <p className='mt-0.5 text-11 text-line-400'>{hint}</p>}
        {hintNode}
      </div>
      <div className='text-right'>{children}</div>
    </div>
  )
}

Row.defaultProps = {
  hint: undefined,
  hintNode: undefined,
}

function formatDate(ts?: number): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function Stepper({
  value,
  min,
  label,
  onChange,
}: {
  value: number
  min: number
  label: string
  onChange: (n: number) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  return (
    <div className='flex items-center gap-2'>
      <button
        type='button'
        aria-label='Decrease'
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
        className={STEP_BTN}
      >
        <RemoveIcon sx={{ fontSize: 14 }} />
      </button>
      <input
        type='text'
        inputMode='numeric'
        aria-label={label}
        value={draft ?? value}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={() => {
          if (draft !== null) onChange(Math.max(min, Number(draft) || 0))
          setDraft(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        className='w-14 rounded-btn border-1.5 border-line-200 bg-white py-0.5 text-center font-display text-sm font-bold text-ink outline-none transition focus:border-brand focus:shadow-focus'
      />
      <button
        type='button'
        aria-label='Increase'
        onClick={() => onChange(value + 1)}
        className={STEP_BTN}
      >
        <AddIcon sx={{ fontSize: 14 }} />
      </button>
    </div>
  )
}

function PurchaseDetails({
  planName,
  priceDisplay,
  originalPriceDisplay,
  discountLabel,
  includedLabel,
  includedValue,
  extraLabel,
  extraUnit,
  seatPrice,
  originalSeatPrice,
  count,
  minCount,
  onCountChange,
  billingLabel,
  expiresAt,
  total,
  originalTotal,
  perSeat,
}: PurchaseDetailsProps) {
  return (
    <div className='overflow-hidden rounded-2xl border border-line-200 bg-white shadow-card'>
      <div className='bg-line-100 px-5 py-3'>
        <p className='text-10 font-bold uppercase tracking-caps text-ink-500'>
          Purchase Details
        </p>
      </div>

      <div className='px-5'>
        <Row label='Plan'>
          <span className='font-display text-13 font-bold text-ink'>
            {planName}
          </span>
        </Row>
        <Row label='Price'>
          <span className='inline-flex items-center justify-end gap-2'>
            {discountLabel && <DiscountPill label={discountLabel} />}
            {originalPriceDisplay && (
              <span className='text-13 text-line-400 line-through'>
                {originalPriceDisplay}
              </span>
            )}
            <span className='font-display text-13 font-bold text-ink'>
              {priceDisplay}
            </span>
          </span>
        </Row>
        {includedValue !== undefined && (
          <Row
            label={includedLabel}
            hint={
              seatPrice !== undefined
                ? `You can purchase ${extraLabel.toLowerCase()} in the field below.`
                : undefined
            }
          >
            <span className='text-13 text-ink'>
              <b>{includedValue}</b>{' '}
              <span className='text-success-700'>included</span>
            </span>
          </Row>
        )}
        {seatPrice !== undefined && (
          <Row
            label={extraLabel}
            hint={
              seatPrice && !originalSeatPrice
                ? `$${seatPrice} / ${extraUnit}`
                : undefined
            }
            hintNode={
              seatPrice && originalSeatPrice ? (
                <div className='mt-0.5 flex items-center gap-1.5 text-11'>
                  <span className='text-line-400 line-through'>
                    ${originalSeatPrice}
                  </span>
                  <span className='font-bold text-success-700'>
                    ${seatPrice}
                  </span>
                  <span className='text-ink-500'>/ {extraUnit}</span>
                </div>
              ) : undefined
            }
          >
            <Stepper
              value={count}
              min={minCount}
              label={extraLabel}
              onChange={onCountChange}
            />
          </Row>
        )}
        <Row label='Billing period'>
          <span className='font-display text-13 font-bold text-ink'>
            {billingLabel}
          </span>
        </Row>
        <Row label='Plan expires'>
          <span className='inline-flex items-center gap-1.5 rounded-pill border border-success-200 bg-success-50 px-2.5 py-1 text-11 font-semibold text-success-700'>
            <CalendarTodayIcon sx={{ fontSize: 13 }} />
            {formatDate(expiresAt)}
          </span>
        </Row>
      </div>

      <div className='bg-line-100 px-5 py-4'>
        <div className='flex items-center justify-between'>
          <span className='text-13 text-ink-500'>Total</span>
          <span className='flex items-baseline gap-2'>
            {originalTotal != null && (
              <span className='text-sm text-line-400 line-through'>
                ${originalTotal.toFixed(2)}
              </span>
            )}
            <span className='font-display text-20 font-extrabold text-ink'>
              ${total.toFixed(2)}
            </span>
          </span>
        </div>
        <p className='mt-1 text-right text-11 text-ink-500'>
          *Taxes or service fees may be applied at checkout
        </p>
      </div>
    </div>
  )
}

PurchaseDetails.defaultProps = {
  includedValue: undefined,
  originalPriceDisplay: undefined,
  discountLabel: undefined,
  seatPrice: undefined,
  originalSeatPrice: undefined,
  originalTotal: undefined,
  expiresAt: undefined,
}

export default PurchaseDetails
